import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef } from "react";
import { paymentMethodsOptions } from "@/api/cashier/@tanstack/react-query.gen";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useMethod } from "./useMethod";

export const useInitPaymentMethods = () => {
  const { selectedAccount } = useAccount();
  const {
    setSelectedPaymentMethod,
    setSelectedPaymentMethodGroup,
    getPaymentMethodById,
    setPaymentMethodListOfTradingAccount,
  } = useMethod();
  const hasSetInitialSelection = useRef(false);
  const hasFetchedOnce = useRef(false);
  const hasSettled = useRef(false);

  useLayoutEffect(() => {
    hasSetInitialSelection.current = false;
    hasFetchedOnce.current = false;
    hasSettled.current = false;
  }, [selectedAccount?.accountId, selectedAccount?.tradingPlatform]);

  //TODO: invalid the query instead of refetching it manually
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    ...paymentMethodsOptions({
      query: {
        "external-trading-account-id": selectedAccount?.accountId ?? 0,
        "trading-platform": selectedAccount?.tradingPlatform ?? "MT4",
      },
    }),
    enabled: Boolean(selectedAccount),
    staleTime: 30_000,
  });

  // Keep reporting isLoading until the first fetch settles, even if the query is refetched in the background.
  // This avoids a flash of stale data when the user navigates back to this page after a previous visit.
  if (isFetching) {
    hasFetchedOnce.current = true;
  } else if (hasFetchedOnce.current) {
    hasSettled.current = true;
  }

  const isLoadingInitial = isLoading || (isFetching && !hasSettled.current);

  useEffect(() => {
    const paymentMethods = data?.arrangement;
    if (paymentMethods) {
      if (!hasSetInitialSelection.current) {
        hasSetInitialSelection.current = true;
        const latestPaymentMethodId =
          data?.last_used_payment_method?.payment_method_id;
        if (latestPaymentMethodId) {
          const lastPaymentMethod = getPaymentMethodById(
            paymentMethods,
            latestPaymentMethodId
          );
          setSelectedPaymentMethod(lastPaymentMethod?.method);
          setSelectedPaymentMethodGroup(lastPaymentMethod?.group);
        }
      }
      setPaymentMethodListOfTradingAccount(paymentMethods);
    }
  }, [
    data,
    getPaymentMethodById,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodGroup,
    setPaymentMethodListOfTradingAccount,
  ]);

  return { isLoading: isLoadingInitial, isFetching, error, data, refetch };
};
